import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Table, Button, Space, Typography, Spin, Popover, Input, List } from "antd";
import { connect } from "react-redux";
import axios from "axios";
import debounce from "lodash/debounce";

const { Title } = Typography;

const OwnerInfo = props => {
  const [assignedOwners, setAssignedOwners] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const listRef = useRef(null);
  const isEditMode = props.isEditMode || false;
  const PAGE_SIZE = 10;

  // Fetch assigned owners when enterprise changes
  useEffect(() => {
    const ownerIds = props.record?.enterprise?.ownersIds;
    fetchAssignedOwners(ownerIds);
  }, [props.record?.enterprise?.ownersIds]);

  const fetchAssignedOwners = async (ownerIds) => {
    if (!ownerIds || ownerIds.length === 0) {
      setAssignedOwners([]);
      return;
    }
    setLoading(true);
    try {
      const owners = await Promise.all(
        ownerIds.map(id =>
          axios.get(`${process.env.REACT_APP_API_ENDPOINT}/owners/${id}`)
            .then(r => r.data)
            .catch(() => null)
        )
      );
      setAssignedOwners(owners.filter(o => o !== null));
    } finally {
      setLoading(false);
    }
  };

  // Load initial owners (first page, excluding already assigned)
  const loadInitialOwners = useCallback((currentAssigned) => {
    setSearchLoading(true);
    setCurrentPage(0);
    setHasMore(true);
    axios.get(`${process.env.REACT_APP_API_ENDPOINT}/owners/`, {
      params: { size: PAGE_SIZE, page: 0 }
    }).then(response => {
      const available = (response.data.content || []).filter(
        owner => !currentAssigned.some(a => a.id === owner.id)
      );
      setSearchResults(available);
      setHasMore(!response.data.last);
    }).catch(() => {
      setSearchResults([]);
      setHasMore(false);
    }).finally(() => {
      setSearchLoading(false);
    });
  }, [PAGE_SIZE]);

  // Load more owners (next page) - works for both browse and search
  const loadMoreOwners = useCallback(() => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const params = { size: PAGE_SIZE, page: nextPage };
    if (searchValue) {
      params.fullName = searchValue;
    }
    axios.get(`${process.env.REACT_APP_API_ENDPOINT}/owners/`, { params })
      .then(response => {
        const newOwners = (response.data.content || []).filter(
          owner => !assignedOwners.some(a => a.id === owner.id)
        );
        setSearchResults(prev => [...prev, ...newOwners]);
        setCurrentPage(nextPage);
        setHasMore(!response.data.last);
      }).catch(() => {
        setHasMore(false);
      }).finally(() => {
        setLoadingMore(false);
      });
  }, [loadingMore, hasMore, searchValue, currentPage, PAGE_SIZE, assignedOwners]);

  // Handle popover open/close
  const handlePopoverOpenChange = useCallback((open) => {
    setPopoverOpen(open);
    if (open) {
      setSearchValue("");
      setCurrentPage(0);
      setHasMore(true);
      loadInitialOwners(assignedOwners);
    }
  }, [assignedOwners, loadInitialOwners]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreOwners();
    }
  }, [loadMoreOwners]);

  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce((value, currentAssigned, loadInitial, pageSize) => {
      if (!value) {
        // Load initial owners when search is cleared
        loadInitial(currentAssigned);
        return;
      }
      setCurrentPage(0);
      setHasMore(true);
      axios.get(`${process.env.REACT_APP_API_ENDPOINT}/owners/`, {
        params: { fullName: value, size: pageSize, page: 0 }
      }).then(response => {
        const available = (response.data.content || []).filter(
          owner => !currentAssigned.some(a => a.id === owner.id)
        );
        setSearchResults(available);
        setHasMore(!response.data.last);
      }).catch(() => {
        setSearchResults([]);
        setHasMore(false);
      }).finally(() => {
        setSearchLoading(false);
      });
    }, 300),
    []
  );

  const handleSearch = useCallback((value) => {
    setSearchLoading(true);
    debouncedSearch(value, assignedOwners, loadInitialOwners, PAGE_SIZE);
  }, [debouncedSearch, assignedOwners, loadInitialOwners, PAGE_SIZE]);

  const handleSearchInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchValue(value);
    handleSearch(value);
  }, [handleSearch]);

  const handleAdd = (ownerId) => {
    const owner = searchResults.find(o => o.id === ownerId);
    if (owner) {
      const newAssigned = [...assignedOwners, owner];
      setAssignedOwners(newAssigned);
      props.parentCallback(newAssigned.map(o => o.id));
      setSearchResults([]);
      setPopoverOpen(false);
      setSearchValue("");
    }
  };

  const handleRemove = (ownerId) => {
    const newAssigned = assignedOwners.filter(o => o.id !== ownerId);
    setAssignedOwners(newAssigned);
    props.parentCallback(newAssigned.map(o => o.id));
  };

  const baseColumns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      width: '25%',
    },
    {
      title: "Fiscal Code",
      dataIndex: "fiscalCode",
      width: '20%',
    },
    {
      title: "Email",
      dataIndex: "email",
      width: '30%',
    },
    {
      title: "Phone",
      dataIndex: "phone",
      width: '15%',
    }
  ];

  const actionColumn = {
    title: "Action",
    key: "action",
    width: '10%',
    render: (_, record) => (
      <Button size="small" danger onClick={() => handleRemove(record.id)}>
        Remove
      </Button>
    )
  };

  const columns = isEditMode ? [...baseColumns, actionColumn] : baseColumns;

  const popoverContent = (
    <div style={{ width: 350 }}>
      <Input.Search
        placeholder="Search by name..."
        value={searchValue}
        onChange={handleSearchInputChange}
        loading={searchLoading}
        style={{ marginBottom: 12 }}
        allowClear
      />
      <Spin spinning={searchLoading}>
        <div
          ref={listRef}
          onScroll={handleScroll}
          style={{ maxHeight: 250, overflowY: 'auto' }}
        >
          <List
            size="small"
            dataSource={searchResults}
            locale={{ emptyText: 'No owners found' }}
            renderItem={(owner) => (
              <List.Item
                key={owner.id}
                onClick={() => handleAdd(owner.id)}
                style={{ cursor: 'pointer', padding: '8px 12px', borderRadius: 4 }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{owner.fullName}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {owner.fiscalCode || 'No fiscal code'}
                  </div>
                </div>
              </List.Item>
            )}
          />
          {loadingMore && (
            <div style={{ textAlign: 'center', padding: 8 }}>
              <Spin size="small" />
            </div>
          )}
          {!hasMore && searchResults.length > 0 && (
            <div style={{ textAlign: 'center', padding: 8, color: '#888', fontSize: 12 }}>
              No more owners
            </div>
          )}
        </div>
      </Spin>
    </div>
  );

  return (
    <Spin spinning={loading}>
      <div style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={5} style={{ margin: 0 }}>
            Assigned Owners ({assignedOwners.length})
          </Title>
          {isEditMode && (
            <Popover
              content={popoverContent}
              title="Select Owner"
              trigger="click"
              open={popoverOpen}
              onOpenChange={handlePopoverOpenChange}
              placement="bottomRight"
            >
              <Button type="primary">Add owner...</Button>
            </Popover>
          )}
        </Space>
      </div>
      <Table
        size="small"
        columns={columns}
        dataSource={assignedOwners}
        rowKey={record => record.id}
        pagination={false}
        locale={{ emptyText: 'No owners assigned' }}
      />
    </Spin>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    startLoading: () => dispatch({ type: "START_LOADING" }),
    stopLoading: val => dispatch({ type: "STOP_LOADING", status: val })
  };
};

export default connect(null, mapDispatchToProps)(OwnerInfo);
